// api.js
// Single source of truth for all HTTP calls from the frontend to the FastAPI backend.
// No component or hook may call Supabase or Anthropic directly — everything goes here.
// Automatically attaches the user's JWT Authorization header to every request.

const BASE_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://rocket-fuel-ai-production.up.railway.app'
    : 'http://localhost:8000';

// Module-level token store. Set by MemoryContext on login/logout.
let _authToken = null;

export function setAuthToken(token) {
  _authToken = token;
}

async function request(method, path, body = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (_authToken) {
    headers['Authorization'] = `Bearer ${_authToken}`;
  }

  const options = { method, headers };
  if (body !== null) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(`${BASE_URL}${path}`, options);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(errorBody.detail || `Request failed with status ${response.status}`);
  }

  // Return null for 204 No Content responses
  if (response.status === 204) return null;
  return response.json();
}

// Auth
export const login = (email, password) => request('POST', '/auth/login', { email, password });
export const signup = (email, password) => request('POST', '/auth/signup', { email, password });
export const logout = () => request('POST', '/auth/logout');

// Memory
export const getMemory = () => request('GET', '/memory');
export const updateMemory = (patch) => request('PATCH', '/memory', patch);

// Agents — legacy stubs (kept for backwards compatibility)
export const sendAgentMessage = (pillar, message) =>
  request('POST', `/agents/${pillar}/message`, { message });
export const endAgentSession = (pillar) =>
  request('POST', `/agents/${pillar}/end`);

// Agents — pillar chat interface (used by Pillar.jsx)
// session_id is a client-generated UUID that persists for the session lifetime.
// conversation_history is the full Anthropic-format message array accumulated
// by the client; the backend is stateless and relies on it for context.
export const sendPillarMessage = (pillar, message, sessionId, conversationHistory) =>
  request('POST', `/agents/${pillar}`, {
    message,
    session_id: sessionId,
    conversation_history: conversationHistory,
  });

export const closePillarSession = (pillar, sessionId, outcomeType, conversationHistory) =>
  request('POST', `/agents/${pillar}/close`, {
    session_id: sessionId,
    outcome_type: outcomeType,
    conversation_history: conversationHistory,
  });

// Documents
export const getDocument = (pillar) => request('GET', `/documents/${pillar}`);
export const listDocuments = () => request('GET', '/documents');
